import type { ToolCall } from '../../../../utils/interface-types';
import type { McpTool } from '../types/api-types';

export type McpToolLike = string | McpTool;

export const getMcpToolName = (tool: McpToolLike): string => {
  if (typeof tool === 'string') return tool;
  return tool.name;
};

export const getMcpToolNames = (tools: McpToolLike[] | undefined): string[] =>
  (tools || []).map(getMcpToolName).filter(Boolean);

export const summarizeInputSchema = (schema?: Record<string, unknown>): string => {
  if (!schema) return 'No parameters';
  const properties = schema.properties as Record<string, unknown> | undefined;
  if (!properties || typeof properties !== 'object') return 'No parameters';
  const keys = Object.keys(properties);
  if (keys.length === 0) return 'No parameters';
  return keys.slice(0, 4).join(', ') + (keys.length > 4 ? '…' : '');
};

export const extractResourceHtml = (
  contents: Array<{ text?: string; blob?: string; mimeType?: string }>,
): string => {
  if (!contents?.length) return '';
  return contents[0].text || '';
};

/** Accepts public URLs and local dev hosts (localhost, 127.0.0.1, ::1). */
export function validateMcpServerUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) {
    return 'URL is required!';
  }

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return 'MCP URL must start with http or https.';
    }
    if (!parsed.hostname) {
      return 'Please enter a valid MCP server URL!';
    }
    return '';
  } catch {
    return 'Please enter a valid MCP server URL!';
  }
}

const MCP_META_FIELD = '_meta';

export type WidgetToolResult = {
  content: Array<Record<string, unknown>>;
  structuredContent?: Record<string, unknown>;
  isError?: boolean;
} & Partial<Record<typeof MCP_META_FIELD, Record<string, unknown>>>;

export type ImageResource = {
  id: string;
  url: string;
  alt?: string;
  href?: string;
};

const IMAGE_URL_RE =
  /https?:\/\/[^\s<>"')\]]+\.(?:png|jpe?g|gif|webp|svg|avif|bmp)(?:\?[^\s<>"')\]]*)?/gi;

function isHttpUrl(value: string): boolean {
  return /^https?:\/\//i.test(value.trim());
}

function looksLikeImageUrl(value: string): boolean {
  return isHttpUrl(value) && /\.(png|jpe?g|gif|webp|svg|avif|bmp)(\?|#|$)/i.test(value.trim());
}

function readMcpMeta(record: Record<string, unknown>): Record<string, unknown> | undefined {
  const meta = record[MCP_META_FIELD];
  return meta && typeof meta === 'object' ? (meta as Record<string, unknown>) : undefined;
}

/** Normalize MCP tools/call result to standard content + structuredContent. */
export function normalizeWidgetToolResult(result: unknown): WidgetToolResult {
  if (result && typeof result === 'object') {
    const record = result as Record<string, unknown>;
    if ('content' in record || 'structuredContent' in record) {
      return {
        content: Array.isArray(record.content)
          ? (record.content as Array<Record<string, unknown>>)
          : [],
        structuredContent:
          record.structuredContent && typeof record.structuredContent === 'object'
            ? (record.structuredContent as Record<string, unknown>)
            : undefined,
        isError: typeof record.isError === 'boolean' ? record.isError : undefined,
        [MCP_META_FIELD]: readMcpMeta(record),
      };
    }
  }

  if (typeof result === 'string') {
    const trimmed = result.trim();
    if (!trimmed) {
      return { content: [{ type: 'text', text: '' }] };
    }
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return normalizeWidgetToolResult(JSON.parse(trimmed));
      } catch {
        // Plain text fallback.
      }
    }
    return { content: [{ type: 'text', text: trimmed }] };
  }

  return {
    content: [{ type: 'text', text: JSON.stringify(result ?? '') }],
  };
}

export function coerceToolResult(result: unknown): unknown {
  if (result == null) {
    return result;
  }
  if (typeof result === 'string') {
    const trimmed = result.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return JSON.parse(trimmed);
      } catch {
        return result;
      }
    }
    return result;
  }
  return result;
}

export function extractImageUrlsFromText(text: string): string[] {
  if (!text) {
    return [];
  }
  const found = new Set<string>();
  const matcher = new RegExp(IMAGE_URL_RE.source, IMAGE_URL_RE.flags);
  let match = matcher.exec(text);
  while (match) {
    const url = match[0].trim();
    if (looksLikeImageUrl(url)) {
      found.add(url);
    }
    match = matcher.exec(text);
  }
  return [...found];
}

export function embedBareImageUrlsInMarkdown(content: string): string {
  if (!content) {
    return content;
  }
  return content.replace(IMAGE_URL_RE, (url) => (looksLikeImageUrl(url) ? `![](${url})` : url));
}

function imagesFromMcpContent(content: Array<Record<string, unknown>>): ImageResource[] {
  const images: ImageResource[] = [];

  content.forEach((block, index) => {
    const { type } = block;

    if (type === 'image' && typeof block.url === 'string' && isHttpUrl(block.url)) {
      images.push({
        id: `img-${index}`,
        url: block.url.trim(),
        alt: typeof block.alt === 'string' ? block.alt : undefined,
      });
      return;
    }

    if (type === 'resource' && block.resource && typeof block.resource === 'object') {
      const resource = block.resource as Record<string, unknown>;
      const uri = typeof resource.uri === 'string' ? resource.uri.trim() : '';
      const mimeType =
        (typeof resource.mimeType === 'string' && resource.mimeType) ||
        (typeof block.mimeType === 'string' && block.mimeType) ||
        '';

      if (uri && isHttpUrl(uri) && (mimeType.startsWith('image/') || looksLikeImageUrl(uri))) {
        images.push({
          id: `resource-${index}`,
          url: uri,
          alt: typeof resource.name === 'string' ? resource.name : undefined,
          href: uri,
        });
      }
      return;
    }

    if (type === 'text' && typeof block.text === 'string') {
      extractImageUrlsFromText(block.text).forEach((url, imageIndex) => {
        images.push({
          id: `text-${index}-${imageIndex}`,
          url,
          alt: 'MCP result',
        });
      });
    }
  });

  return images;
}

function parseToolResultImages(result: unknown): ImageResource[] {
  const coerced = coerceToolResult(result);
  const images: ImageResource[] = [];

  if (typeof result === 'string') {
    extractImageUrlsFromText(result).forEach((url, index) => {
      images.push({ id: `raw-${index}`, url, alt: 'MCP result' });
    });
  }

  if (coerced && typeof coerced === 'object') {
    const record = coerced as Record<string, unknown>;
    if (Array.isArray(record.content)) {
      images.push(...imagesFromMcpContent(record.content as Array<Record<string, unknown>>));
    }
  } else if (typeof coerced === 'string') {
    extractImageUrlsFromText(coerced).forEach((url, index) => {
      images.push({ id: `coerced-${index}`, url, alt: 'MCP result' });
    });
  }

  return images.filter(
    (img, index, arr) => arr.findIndex((other) => other.url === img.url) === index,
  );
}

export function mergeToolWidgetResults(existing: unknown, incoming: unknown): unknown {
  return incoming != null ? coerceToolResult(incoming) : coerceToolResult(existing);
}

export function toolCallHasWidget(toolCall: {
  has_widget?: boolean;
  mcp_id?: number;
  ui_resource_uri?: string;
}): boolean {
  return Boolean(toolCall.has_widget && toolCall.mcp_id && toolCall.ui_resource_uri);
}

export function extractToolCallImages(toolCall: {
  widget_result?: unknown;
  message?: string;
}): ImageResource[] {
  const images: ImageResource[] = [];

  if (toolCall.widget_result !== undefined) {
    images.push(...parseToolResultImages(toolCall.widget_result));
  }

  if (toolCall.message) {
    images.push(...parseToolResultImages(toolCall.message));
  }

  return images.filter(
    (img, index, arr) => arr.findIndex((other) => other.url === img.url) === index,
  );
}

const TOOL_DISPLAY_NAMES: Record<string, string> = {
  web_search: 'Web Search',
  code_interpreter: 'Code Interpreter',
  get_context_from_vector_store: 'Knowledge base',
};

export function formatToolDisplayName(toolName: string): string {
  const match = /^mcp_exec_\d+__(.+)$/.exec(toolName);
  if (match) {
    return match[1];
  }
  return TOOL_DISPLAY_NAMES[toolName] || toolName;
}

export function formatToolLiveStatus(toolName: string): string {
  if (toolName === 'web_search') {
    return 'Searching the web…';
  }
  if (toolName === 'code_interpreter') {
    return 'Running code…';
  }
  if (toolName === 'get_context_from_vector_store') {
    return 'Searching knowledge base…';
  }
  return `Calling ${formatToolDisplayName(toolName)}…`;
}

export function toolCallIsComplete(toolCall: { is_loading?: boolean }): boolean {
  return !toolCall.is_loading;
}

export function summarizeToolCalls(toolCalls: ToolCall[]): {
  anyLoading: boolean;
  anyError: boolean;
  allComplete: boolean;
} {
  if (!toolCalls.length) {
    return { anyLoading: false, anyError: false, allComplete: false };
  }
  return {
    anyLoading: toolCalls.some((tc) => tc.is_loading),
    anyError: toolCalls.some((tc) => tc.mcp_error),
    allComplete: toolCalls.every((tc) => !tc.is_loading),
  };
}

export function toolCallHasVisualOutput(toolCall: {
  has_widget?: boolean;
  mcp_id?: number;
  ui_resource_uri?: string;
  widget_result?: unknown;
  message?: string;
  mcp_error?: unknown;
}): boolean {
  return (
    Boolean(toolCall.mcp_error) ||
    toolCallHasWidget(toolCall) ||
    extractToolCallImages(toolCall).length > 0
  );
}

export function toolCallHasRenderableContent(toolCall: {
  has_widget?: boolean;
  mcp_id?: number;
  ui_resource_uri?: string;
  widget_result?: unknown;
  message?: string;
  mcp_error?: unknown;
  is_loading?: boolean;
}): boolean {
  return (
    Boolean(toolCall.is_loading) ||
    Boolean(toolCall.mcp_error) ||
    toolCallHasWidget(toolCall) ||
    extractToolCallImages(toolCall).length > 0
  );
}

export function patchToolCallResult(
  toolCalls: ToolCall[],
  patch: {
    tool_name: string;
    tool_id?: string;
    mcp_id?: number;
    result?: unknown;
    error?: unknown;
    arguments?: unknown;
    mergeResult?: boolean;
    has_widget?: boolean;
    ui_resource_uri?: string;
  },
): ToolCall[] {
  const existingIndex = toolCalls.findIndex(
    (tc) => tc.tool_name === patch.tool_name && (!patch.tool_id || tc.id === patch.tool_id),
  );
  const existing = existingIndex >= 0 ? toolCalls[existingIndex] : undefined;

  let widgetResult = existing?.widget_result;
  if (patch.result !== undefined) {
    widgetResult = patch.mergeResult
      ? mergeToolWidgetResults(existing?.widget_result, patch.result)
      : coerceToolResult(patch.result);
  }

  const next: ToolCall = {
    tool_name: patch.tool_name,
    message: existing?.message || '',
    id: patch.tool_id || existing?.id,
    mcp_id: patch.mcp_id ?? existing?.mcp_id,
    has_widget: patch.has_widget ?? existing?.has_widget,
    ui_resource_uri: patch.ui_resource_uri ?? existing?.ui_resource_uri,
    widget_arguments: patch.arguments ?? existing?.widget_arguments,
    widget_result: widgetResult,
    mcp_error: patch.error ?? existing?.mcp_error,
    is_loading: false,
  };

  if (existingIndex >= 0) {
    const updated = [...toolCalls];
    updated[existingIndex] = { ...existing, ...next };
    return updated;
  }
  return [...toolCalls, next];
}

export function getToolCallErrorText(error: unknown): string {
  if (!error) {
    return '';
  }
  if (typeof error === 'string') {
    return error;
  }
  if (typeof error === 'object') {
    const record = error as { content?: { text?: string }[]; message?: string };
    if (record.content?.[0]?.text) {
      return record.content[0].text;
    }
    if (typeof record.message === 'string') {
      return record.message;
    }
    return JSON.stringify(error);
  }
  return String(error);
}

export function isMcpConnectivityMessage(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('mcp access denied') ||
    lower.includes('could not load mcp') ||
    lower.includes('mcp server is unreachable') ||
    lower.includes('mcp server is not running') ||
    lower.includes('reconnect oauth') ||
    lower.includes('error retrieving tool list from mcp') ||
    lower.includes('could not connect to mcp') ||
    lower.includes('could not reach mcp')
  );
}

export function isMcpConnectivityError(error: unknown): boolean {
  if (!error) {
    return false;
  }
  let text: string;
  if (typeof error === 'string') {
    text = error;
  } else if (typeof error === 'object' && error !== null && 'message' in error) {
    text = String((error as { message?: unknown }).message ?? '');
  } else {
    text = JSON.stringify(error);
  }
  return isMcpConnectivityMessage(text);
}

export function parseMcpExecId(toolName: string): number | null {
  const match = /^mcp_exec_(\d+)__/.exec(toolName);
  return match ? Number(match[1]) : null;
}

export function mcpShowsToolList(mcp: { status: string; tools?: unknown[] }): boolean {
  return mcp.status === 'active' && Array.isArray(mcp.tools) && mcp.tools.length > 0;
}

/** User manually turned the MCP off — can be re-enabled with the toggle. */
export function mcpIsUserDisabled(status: string): boolean {
  return status === 'inactive';
}

/** Server/auth failure — user should reconnect or re-authenticate. */
export function mcpNeedsReconnect(status: string): boolean {
  return status === 'unauthorize' || status === 'offline';
}

/** Toggle is shown for active MCPs and user-disabled ones. */
export function mcpCanUserToggle(status: string): boolean {
  return status === 'active' || status === 'inactive';
}

export function mcpIsConnected(status: string): boolean {
  return status === 'active';
}

/** Show reconnect + delete instead of toggle when this MCP hit a connection/auth error. */
export function mcpShowsReconnectActions(
  mcp: { status: string },
  panelErrorMessage?: string,
): boolean {
  if (panelErrorMessage) {
    return true;
  }
  return mcpNeedsReconnect(mcp.status);
}

export function parseMcpNameFromWarning(message: string): string | null {
  const match = /^([^:]+):\s/.exec(message.trim());
  return match ? match[1].trim() : null;
}

export function resolveMcpIdFromNotice(
  message: string,
  mcps: Array<{ id: number; name: string }>,
): number | undefined {
  const name = parseMcpNameFromWarning(message);
  if (!name) {
    return undefined;
  }
  const exact = mcps.find((mcp) => mcp.name === name);
  if (exact) {
    return exact.id;
  }
  return mcps.find((mcp) => mcp.name.toLowerCase() === name.toLowerCase())?.id;
}

export function formatMcpUserNotice(message: string): string {
  const lower = message.toLowerCase();
  const name = parseMcpNameFromWarning(message) || 'MCP';

  if (
    lower.includes('access denied') ||
    lower.includes('unauthorized') ||
    lower.includes('reconnect oauth')
  ) {
    return `${name} needs to be reconnected in MCP settings.`;
  }
  if (
    lower.includes('unreachable') ||
    lower.includes('not running') ||
    lower.includes('could not reach') ||
    lower.includes('connection refused')
  ) {
    return `${name} is offline. Use Reconnect to try again.`;
  }
  return `${name} couldn't connect. Use Reconnect to try again.`;
}

export function formatMcpSidePanelFailure(message: string): string {
  return formatMcpUserNotice(message).replace(
    'Use Reconnect to try again.',
    'Use the Reconnect button to open MCP settings.',
  );
}

export function formatMcpBannerMessage(warnings: string[]): string {
  const names = [...new Set(warnings.map((warning) => parseMcpNameFromWarning(warning) || 'MCP'))];
  let label = 'MCP';
  if (names.length === 1) {
    const [onlyName] = names;
    label = onlyName;
  } else if (names.length > 1) {
    const [lastName] = names.slice(-1);
    label = `${names.slice(0, -1).join(', ')} and ${lastName}`;
  }

  return `${label} couldn't connect. Your reply was sent without it — reconnect in MCP settings to try again.`;
}

export function appendMcpWarning(existing: string[] | undefined, warning: string): string[] {
  const trimmed = warning.trim();
  if (!trimmed) {
    return existing ?? [];
  }
  const next = [...(existing ?? [])];
  if (!next.includes(trimmed)) {
    next.push(trimmed);
  }
  return next;
}

export function isToolCallLoading(
  isLoadingFlag: boolean | undefined,
  hasResult: boolean,
  hasError: boolean,
): boolean {
  return Boolean(isLoadingFlag) && !hasResult && !hasError;
}

export function finalizeToolCalls(toolCalls: ToolCall[]): ToolCall[] {
  return toolCalls.map((toolCall) =>
    toolCall.is_loading ? { ...toolCall, is_loading: false } : toolCall,
  );
}
